// roughly taken from:
// https://github.com/johnhornsby/motion-tween/blob/d06852680d82f9f98164281b18bda708bef43ed1/src/animators/SpringRK4.js
const MARGIN = 0.5;
const MAX_FRAMETIME = 20 / 1000;

/**
 * glossary:
 * x = the spring length, as in the difference of current value to targetValue
 *     this converges to 0
 * v = velocity
 * dt = frameTime
 * a = acceleration, in this case the function that calculates the acceleration
 */
export default class Spring {
	constructor(setter, config, margin = MARGIN) {
		this.setter = setter;
		this.config = config;
		this.margin = margin;
		this.value = this.velocity = this.targetValue = 0;
		this.a = (x, v, dt) => this.acceleration(x, v, dt);
	}

	acceleration(x, v) {
		const [stiffness, damping] = this.config;
		return -stiffness * x - damping * v;
	}

	rk4(x, v, a, dt) {
		const x1 = x;
		const v1 = v;
		const a1 = a(x1, v1, 0);

		const x2 = x + 0.5 * v1 * dt;
		const v2 = v + 0.5 * a1 * dt;
		const a2 = a(x2, v2, dt / 2);

		const x3 = x + 0.5 * v2 * dt;
		const v3 = v + 0.5 * a2 * dt;
		const a3 = a(x3, v3, dt / 2);

		const x4 = x + v3 * dt;
		const v4 = v + a3 * dt;
		const a4 = a(x4, v4, dt);

		const xf = x + dt / 6 * (v1 + 2 * v2 + 2 * v3 + v4);
		const vf = v + dt / 6 * (a1 + 2 * a2 + 2 * a3 + a4);

		return {xf, vf};
	}

	step(frameTime) {
		const {targetValue} = this;
		if (this.value === targetValue) { return true; }

		// the physics simulation gets very unstable when the frame time gets
		// too long, so rather split this up into more iterations in that case…
		while (frameTime > 0) {
			const dt = Math.min(frameTime, MAX_FRAMETIME);
			const x = targetValue - this.value;
			const v = this.velocity;

			const {xf, vf} = this.rk4(x, v, this.a, dt);

			this.velocity = vf;
			this.value = targetValue - xf;

			if (
				Math.abs(this.velocity * MAX_FRAMETIME) < this.margin &&
				Math.abs(this.value - targetValue) < this.margin
			) {
				this.value = targetValue;
				this.velocity = 0;

				// We return false here, so that the spring system will only consider
				// this spring as settled on the next tick, in case the corresponding
				// setter is async, such as the scroll position is -_-
				return false;
			}

			frameTime -= dt;
		}

		return false;
	}
}



// WEBPACK FOOTER //
// ./lib/pagehandler/math/spring.js